import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Session, User } from '@supabase/supabase-js';
import { useLanguage } from '@/contexts/LanguageContext';

export interface WishlistItem {
  id: string;
  name: string;
  brand: string;
  price: number;
  image: string;
}

interface WishlistContextType {
  items: WishlistItem[];
  addToWishlist: (product: WishlistItem) => Promise<void>;
  removeFromWishlist: (id: string) => Promise<void>;
  isInWishlist: (id: string) => boolean;
  clearWishlist: () => Promise<void>;
  loading: boolean;
  user: User | null;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

interface WishlistProviderProps {
  children: ReactNode;
}

export const WishlistProvider: React.FC<WishlistProviderProps> = ({ children }) => {
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const { toast } = useToast();
  const { t } = useLanguage();

  const loadWishlistFromDatabase = async (userToLoad?: any) => {
    const currentUser = userToLoad || session?.user || user;
    console.log('loadWishlistFromDatabase called with user:', currentUser?.id);
    if (!currentUser) {
      console.log('No user found, aborting wishlist load');
      return;
    }

    try {
      console.log('Starting wishlist load from database for user:', currentUser.id);
      setLoading(true);
      
      // Get wishlist items without join - we'll fetch product details separately
      const { data: wishlistData, error } = await supabase
        .from('wishlist_items')
        .select('product_id')
        .eq('user_id', currentUser.id);

      console.log('Wishlist database query result:', { wishlistData, error });

      // If join fails, try without join and get product details separately
      if (error || !wishlistData) {
        console.log('Join query failed, trying alternative approach:', error);
        const { data: simpleWishlistData, error: simpleError } = await supabase
          .from('wishlist_items')
          .select('product_id')
          .eq('user_id', currentUser.id);

        if (simpleError) {
          console.error('Error loading wishlist:', simpleError);
          toast({
            title: "Klaida",
            description: "Nepavyko užkrauti norų sąrašo",
            variant: "destructive",
          });
          return;
        }

        // Get product details separately
        if (wishlistData && wishlistData.length > 0) {
          const productIds = wishlistData.map(item => item.product_id);
          const { data: productsData, error: productsError } = await (supabase as any)
            .from('products_public')
            .select('id, name, brand, price, image')
            .in('id', productIds);

          if (productsError) {
            console.error('Error loading products:', productsError);
            toast({
              title: "Klaida",
              description: "Nepavyko užkrauti prekių informacijos",
              variant: "destructive",
            });
            return;
          }

          // Combine wishlist and product data
          const wishlistDataWithProducts = wishlistData.map(wishlistItem => ({
            product_id: wishlistItem.product_id,
            products: productsData?.find(product => product.id === wishlistItem.product_id) || {
              id: wishlistItem.product_id,
              name: 'Unknown Product',
              brand: 'Unknown Brand',
              price: 0,
              image: ''
            }
          }));

          const wishlistItems: WishlistItem[] = wishlistDataWithProducts.map((item: any) => ({
            id: item.product_id,
            name: item.products?.name || 'Unknown Product',
            brand: item.products?.brand || 'Unknown Brand',
            price: item.products?.price || 0,
            image: item.products?.image || '/placeholder.svg',
          }));

          console.log('Loaded wishlist items:', wishlistItems);
          setItems(wishlistItems);
        } else {
          setItems([]);
        }
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize auth state
  useEffect(() => {
    let hasLoadedWishlist = false;
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Wishlist auth state changed:', event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);
        
        // Load wishlist when user is authenticated and wishlist hasn't been loaded yet
        if (session?.user && !hasLoadedWishlist) {
          console.log('Loading wishlist due to auth event:', event);
          hasLoadedWishlist = true;
          setTimeout(() => {
            loadWishlistFromDatabase(session.user);
          }, 0);
        } else if (!session?.user) {
          // Clear wishlist when user logs out
          console.log('User logged out, clearing wishlist...');
          hasLoadedWishlist = false;
          setItems([]);
        }
      }
    );

    // Check for existing session immediately
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Wishlist initial session check:', session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user && !hasLoadedWishlist) {
        console.log('Loading wishlist from initial session');
        hasLoadedWishlist = true;
        loadWishlistFromDatabase(session.user);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const addToWishlist = async (product: WishlistItem) => {
    if (!user) {
      toast({
        title: t.wishlistMessages.loginRequired,
        description: t.wishlistMessages.loginToAddToWishlist,
        variant: "destructive",
      });
      return;
    }

    // Check if already in wishlist
    if (isInWishlist(product.id)) {
      toast({
        title: t.wishlistMessages.alreadyInWishlist,
        description: t.wishlistMessages.alreadyInWishlistDesc,
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('wishlist_items')
        .insert({
          user_id: user.id,
          product_id: product.id
        });

      if (error) {
        // Handle the duplicate key error gracefully
        if (error.code === '23505') {
          toast({
            title: "Prekė jau sąraše",
            description: "Ši prekė jau yra jūsų norų sąraše",
            variant: "destructive",
          });
          // Refresh the wishlist to ensure UI is in sync
          await loadWishlistFromDatabase();
        } else {
          console.error('Error adding to wishlist:', error);
          toast({
            title: "Klaida",
            description: "Nepavyko pridėti prekės į norų sąrašą",
            variant: "destructive",
          });
        }
        return;
      }

      setItems(prev => [...prev, product]);
      toast({
        title: "Pridėta į norų sąrašą",
        description: `${product.name} pridėta į norų sąrašą`,
      });
    } catch (error) {
      console.error('Error adding to wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (id: string) => {
    if (!user) return;

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('wishlist_items')
        .delete()
        .eq('user_id', user.id)
        .eq('product_id', id);

      if (error) {
        console.error('Error removing from wishlist:', error);
        toast({
          title: "Klaida",
          description: "Nepavyko pašalinti prekės iš norų sąrašo",
          variant: "destructive",
        });
        return;
      }

      setItems(prev => prev.filter(item => item.id !== id));
      toast({
        title: "Pašalinta",
        description: "Prekė pašalinta iš norų sąrašo",
      });
    } catch (error) {
      console.error('Error removing from wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearWishlist = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('wishlist_items')
        .delete()
        .eq('user_id', user.id);

      if (error) {
        console.error('Error clearing wishlist:', error);
        toast({
          title: "Klaida",
          description: "Nepavyko išvalyti norų sąrašo",
          variant: "destructive",
        });
        return;
      }

      setItems([]);
      toast({
        title: "Norų sąrašas išvalytas",
        description: "Visos prekės pašalintos iš norų sąrašo",
      });
    } catch (error) {
      console.error('Error clearing wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const isInWishlist = (id: string) => {
    return items.some(item => item.id === id);
  };

  return (
    <WishlistContext.Provider value={{
      items,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      clearWishlist,
      loading,
      user
    }}>
      {children}
    </WishlistContext.Provider>
  );
};